<!-- eslint-disable max-len -->
<template>
  <b-modal
    ref="create-confirm-modal"
    hide-footer
    hide-header
    id="upload-image-modal"
    no-close-on-backdrop
    no-close-on-esc
    @keyup.esc="onEsc()"
  >
    <div class="modal__title--tab">
      <a :class="{active: tab == 1}" @click="selectTab(1)">Upload</a>
      <a :class="{active: tab == 2}" @click="selectTab(2)">Existing tracks</a>
    </div>
    <div class="close-modal" @click="hide">
      <i class="fas fa-times" />
    </div>

    <Uploader v-if="isLoading" />

    <div class="tab-content" v-if="tab == 1" style="overflow: hidden;">
      <label
        for="upload-file"
        class="mb-0 horizontal layout center center-justified"
        :class="{'error': error, 'uploaded': uploadDone}"
      >
        <div v-if="!uploadDone">
          <div v-if="!isLoading">
            <i class="fas fa-volume-up fa-4x" />
            <h3 class="dropzone-custom-title">
              Drag and drop, or
              <b>click here</b> to upload your audio file.
            </h3>
            <div class="subtitle">Maximum file size is 15 MB.</div>
            <div class="p-subtitle">
              .mp3 only
              <span class="title-table-presentation-tooltip result-layout-tooltip">
                <b-button
                  v-tooltip="'Tip: If your audio file is in a different format, you can use an online converter to quickly convert it into MP3'"
                  class="tooltip-btn"
                />
                <i class="fas fa-question-circle" />
              </span>
            </div>

            <div class="error-text text-danger" v-if="error">{{ error }}</div>
          </div>
        </div>
        <div class="image-upload-done" v-if="uploadDone">
          <img :src="$store.state.slideActive.image.large" alt>
        </div>
        <b-form-file
          v-model="file"
          :state="Boolean(file)"
          choose-label="Attachment2"
          accept="audio/mp3"
          id="upload-file"
          v-if="!isLoading"
          drop-placeholder="drop audio"
          :file-name-formatter="uploadFile"
        />
      </label>
    </div>
    <div class="tab-content" v-show="tab == 2" id="infinite-list">
      <p v-if="!audios.length">
        Audio tracks that have been uploaded to this presentation will be listed here.
      </p>
      <table class="table" v-if="audios.length">
        <thead>
          <tr>
            <th class="border-top-0 w-80px">
              <strong>Slide</strong>
            </th>
            <th class="border-top-0">
              <strong>Name</strong>
            </th>
            <th class="border-top-0 d-none d-sm-table-cell" />
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in audios" :key="index">
            <td class="align-middle">
              Slide {{ item.order }}
            </td>
            <td class="align-middle">
              <a class="pointer" @click="selecAudio(item, $event)">{{ item.name }}</a>
            </td>
            <td class="d-none d-sm-table-cell">
              <audio :src="item.link.url" controls />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import Config from '@/config'
import Socket from '@/socket'
import SlideApi from '@/api/slide'
import Constant from '@/constant'
import Util from '@/util'

// UI
import Uploader from '@/components/UI/loaders/Uploader'

import gaCategories from '@/constant/gaCategories'

const { PRESENTATION_EDITOR } = gaCategories

export default {
  components: {
    Uploader,
  },
  props: ['type'],
  data() {
    return {
      tab: 1,
      text: '',
      file: null,
      error: '',
      isLoading: false,
      uploadDone: false,
      isLoadingImg: false,
      audios: [],
    }
  },
  mounted() {
    this.show()
    this._getVideoData()

    setTimeout(() => {
      // Disable deselect
      const modal = document.getElementById('upload-image-modal')
      if (modal) {
        modal.addEventListener('mouseup', (e) => {
          e.stopPropagation()
        }, true)
      }
    }, 0)
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    _getVideoData() {
      const arr = []
      this.$store.state.presentation.slides.forEach((item, index) => {
        if (!item.audioLink) return

        arr.push({
          name: item.audioName,
          link: item.audioLink,
          order: index + 1,
        })
      })

      this.audios = arr
    },
    onEsc() {
      this.hide()
    },
    async selecAudio(item, event) {
      event.preventDefault()
      const payload = {}
      this.updateSlideActiveAction({
        audioName: item.name,
        audioLink: {
          name: item.link.name,
          url: item.link.url,
        },
      })
      this.hide()
      payload.presentationId = this.$store.state.presentation.id
      payload.accessCode = this.$store.state.presentation.uniqueAccessCode
      payload.socketId = Socket.getSocketId()
      payload.presenting = this.$store.getters.isPresenting || this.$store.getters.isBackstage
      payload.audioLink = item.link.url
      payload.audioName = item.name

      await this.updateSlides(payload)

      Util.gaTrackEvent(
        PRESENTATION_EDITOR,
        this.$store.getters.selectedSlides.length > 1
          ? 'Audio Tab > Select audio for multiple slides'
          : 'Audio Tab > Select audio for single slide',
        `Presentation: ${this.$store.state.presentation.id}`,
      )
    },
    async uploadFile(files) {
      if (!files || !files.length) return
      const file = files[0]
      this.error = ''
      this.isLoading = true
      if (file && file.size > 15728640) {
        this.error = 'Could not upload this file (max size is 15 MB).'
        this.isLoading = false
        return
      } if (file.type.indexOf('audio') == -1) {
        this.error =
          'Could not upload this file (only support audio file mp3).'
        this.isLoading = false
        return
      }

      const ext = file.name.split('.').pop()
      if (ext === 'mp3') {
        const formData = new FormData()
        formData.append('file', file)
        formData.append(
          'accessCode',
          this.$store.state.presentation.uniqueAccessCode,
        )
        formData.append('socketId', Socket.getSocketId())
        formData.append('presentationId', this.$store.state.presentation.id)
        formData.append('slideId', this.$store.state.slideActive.id)
        const headers = new Headers()
        headers.append('Content-Type', 'application/x-www-form-urlencoded')
        const url = Config.workerApi.uploadFileAudio + this.$store.state.presentation.id

        try {
          const res = await this.$http.post(url, formData)
          this.hide()
          this.isLoading = false
          this.uploadDone = true

          const payload = {
            audioLink: res.body.file.link,
            audioName: res.body.file.name,
          }
          await this.updateSlides(payload)
        } catch (err) {
          this.error = 'Something went wrong…'
          this.isLoading = false
        }
      } else {
        this.error = 'Could not upload this file (only support audio file mp3).'
        this.isLoading = false
      }

      Util.gaTrackEvent(
        PRESENTATION_EDITOR,
        this.$store.getters.selectedSlides.length > 1
          ? 'Audio Tab > Upload audio for multiple slides'
          : 'Audio Tab > Upload audio for single slide',
        `Presentation: ${this.$store.state.presentation.id}`,
      )
    },

    async updateSlides(payload) {
      const instantPayload = {}
      Constant.commonSlideAttributes.forEach((attr) => {
        if (payload[attr] !== undefined) {
          instantPayload[attr] = payload[attr]
          // FIXME: Get & Set data should be the same format so we don't have to do this
          if (attr === 'audioLink') {
            instantPayload[attr] = {
              url: instantPayload[attr],
              name: payload.audioName,
            }
          }
        }
      })

      const appliedSlides = this.$store.getters.selectedSlides
      this.$store.dispatch('updateSlideActive', instantPayload)
      const slides = appliedSlides.map((slide) => ({
        id: slide.id,
        ...payload,
      }))

      try {
        this.$store.dispatch('setSyncPresentation', 1)
        await SlideApi.updateSlides({
          presentationId: this.$store.state.presentation.id,
          slides,
        })

        appliedSlides.forEach((slide) => {
          Constant.commonSlideAttributes.forEach((attr) => {
            if (instantPayload[attr] !== undefined) {
              slide[attr] = instantPayload[attr]
            }
          })
        })

        this.$store.dispatch('setSyncPresentation', 2)
      } catch (err) {
        this.$store.dispatch('setSyncPresentation', 3)
        throw err
      }
    },
    show() {
      this.uploadDone = false
      this.$refs['create-confirm-modal'].show()
    },
    hide() {
      this.$emit('hideModal')
    },
    selectTab(index) {
      this.tab = index
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";
.list-video {
  margin-top: 16px;
}
.item-video {
  border: 1px solid #ddd;
  padding: 16px;
  border-bottom: none;
  padding-right: 100px;
  cursor: pointer;

  &:hover {
    background-color: #f1f1f1;
  }

  &:last-child {
    border-bottom: 1px solid #ddd;
  }
}
.list-image {
  position: relative;
  overflow: hidden;
}
.uploading-text {
  font-size: 20px;
  font-weight: bold;
}
.hide-loading-image {
  opacity: 0;
}

.fa-volume-up {
  margin-bottom: 10px;
}

.input-search {
  background-color: #ffffff;
  height: 85px;
  z-index: 1;
  & ::v-deep {
    .spinner {
      position: absolute;
      text-align: center;
      top: 50%;
      right: 5px;
      transform: translateY(-50%);
    }
  }
}
.dropzone-custom-title {
  b {
    color: $primary-color;
  }
}
.fa-image {
  margin-bottom: 24px;
  margin-top: 8px;
}
.p-subtitle {
  color: #b9b9b9;
  position: relative;
  z-index: 99;
}
.subtitle {
  margin-bottom: 6px;
}
.image-upload-done {
  text-align: center;
  img {
    max-height: 400px;
  }
}

.img-info {
  position: absolute;
  bottom: 0;
  background: #0000005e;
  color: #fff;
  font-size: 13px;
  padding: 5px;
  text-align: center;
  display: none;
  width: 100%;
  a {
    color: inherit;
  }
  img {
    width: 50%;
  }
}
.item-image {
  width: 25%;
  float: left;
  padding: 5px;

  .img-bg {
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    height: 160px;
    position: relative;

    a {
      text-decoration: underline !important;
    }

    &:hover {
      cursor: pointer;
      .img-info {
        display: block;
      }
    }
  }
}

audio {
  border: 2px solid #ccc;
  border-radius: 30px;
  height: 32px;
  max-width: 380px;
  display: block;
}
</style>
